import { F0Box, F0Form, f0FormField, useF0FormDefinition } from '@factorialco/f0-react'
import { SendVendorOnboardingInvitationDocument } from 'generated/resources/graphql'
import { z } from 'zod'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

const useSendVendorOnboardingInvitation = () =>
  useMutation(SendVendorOnboardingInvitationDocument, {
    extractorFn: (data) =>
      data.vendorPortalMutations.sendInvitationVendorportalVendorOnboardingInvitation,
  })

interface SendToVendorInlineFormProps {
  taskableId: string
  onSuccess?: () => void
  /** Pre-fill with the email already sent to (shown when task is done). */
  vendorEmail?: string
  /** Pre-fill with the message already sent to the vendor (shown when task is done). */
  vendorMessage?: string
  /** When true, all inputs are disabled and the form cannot be submitted. */
  disabled?: boolean
}

export const SendToVendorInlineForm = ({
  taskableId,
  onSuccess,
  vendorEmail,
  vendorMessage,
  disabled = false,
}: SendToVendorInlineFormProps) => {
  const [sendVendorOnboardingInvitation] = useSendVendorOnboardingInvitation()

  const schema = z.object({
    vendorEmail: f0FormField(z.string().trim().min(1).email(), {
      label: i18n.t('tasks.types.vendor_onboarding.send_to_vendor.email_label'),
      placeholder: i18n.t('tasks.types.vendor_onboarding.send_to_vendor.email_placeholder'),
      disabled,
    }),
    vendorMessage: f0FormField(z.string().max(1000).optional(), {
      label: i18n.t('tasks.types.vendor_onboarding.send_to_vendor.message_label'),
      placeholder: i18n.t('tasks.types.vendor_onboarding.send_to_vendor.message_placeholder'),
      fieldType: 'textarea',
      disabled,
    }),
  })

  const formDefinition = useF0FormDefinition({
    name: 'send-to-vendor-inline',
    schema,
    defaultValues: {
      vendorEmail: vendorEmail ?? '',
      vendorMessage: vendorMessage ?? '',
    },
    onSubmit: async ({ data }) => {
      const result = await sendVendorOnboardingInvitation({
        variables: {
          vendorOnboardingTaskId: taskableId,
          vendorEmail: data.vendorEmail,
          vendorMessage: data.vendorMessage || undefined,
        },
      })
      const handled = handleResult(result, () => {})

      if (!handled.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('tasks.types.vendor_onboarding.send_to_vendor.error'),
        })

        return {
          success: false,
          errors: {},
        }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('tasks.types.vendor_onboarding.send_to_vendor.success'),
      })
      onSuccess?.()
      return { success: true }
    },
    submitConfig: {
      type: 'default',
      label: i18n.t('tasks.types.vendor_onboarding.send_to_vendor.submit'),
      hideSubmitButton: disabled,
    },
  })

  return (
    <F0Box paddingY="md">
      <F0Form formDefinition={formDefinition} />
    </F0Box>
  )
}
